import React, { useState, useEffect, useCallback } from 'react';
import {
  FiX, FiDownload, FiCheckCircle, 
  FiFile,  FiClock, FiSearch, FiChevronLeft, FiChevronRight,
   FiSend
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import api from '../../../api';

const AssignmentSubmissionsModal = ({ isOpen, onClose, assignment, classItem, subject, facultyId }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [gradingData, setGradingData] = useState({ marksObtained: '', feedback: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({ total: 0, graded: 0, pending: 0 });
  const itemsPerPage = 10;

  // Wrapped in useCallback to prevent infinite re-renders while remaining an eligible dependency
  const fetchSubmissions = useCallback(async () => {
    if (!assignment?._id) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/faculty/lms/assignment/${assignment._id}/submissions`);
      if (response.data?.success) {
        setSubmissions(response.data.data.submissions || []);
        setStats({
          total: response.data.data.total || 0,
          graded: response.data.data.graded || 0,
          pending: response.data.data.pending || 0
        });
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [assignment?._id]);

  useEffect(() => {
    if (isOpen) {
      fetchSubmissions();
    }
  }, [isOpen, fetchSubmissions]);

  const handleGrade = async (submissionId) => {
    try {
      const response = await api.post(`/api/faculty/lms/submission/${submissionId}/grade`, {
        marksObtained: parseFloat(gradingData.marksObtained),
        feedback: gradingData.feedback
      });
      if (response.data?.success) {
        setShowGradeModal(false);
        setSelectedSubmission(null);
        setGradingData({ marksObtained: '', feedback: '' });
        fetchSubmissions();
      }
    } catch (error) {
      console.error('Error grading submission:', error);
      alert(error.response?.data?.message || 'Failed to grade submission');
    }
  };

  const handleReturn = async (submissionId) => {
    try {
      const response = await api.put(`/api/faculty/lms/submission/${submissionId}/return`);
      if (response.data?.success) {
        fetchSubmissions();
      }
    } catch (error) {
      console.error('Error returning submission:', error);
      alert('Failed to return submission');
    }
  };

  const handleDownloadAll = () => {
    // Download all submissions as zip - will implement with backend
    alert('Download all submissions feature coming soon');
  };

  const filteredSubmissions = submissions.filter(s => {
    const searchMatch = s.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       s.studentUSN?.toLowerCase().includes(searchTerm.toLowerCase());
    const statusMatch = statusFilter === 'all' ||
                       (statusFilter === 'graded' && s.isGraded) ||
                       (statusFilter === 'pending' && !s.isGraded);
    return searchMatch && statusMatch;
  });

  const paginatedSubmissions = filteredSubmissions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-indigo-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Submissions</h3>
              <p className="text-sm text-indigo-200">{assignment?.title}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadAll}
                className="px-3 py-1.5 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors text-sm flex items-center gap-2"
              >
                <FiDownload className="w-4 h-4" />
                Download All
              </button>
              <button
                onClick={onClose}
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-6 text-sm flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Total:</span>
            <span className="font-semibold">{stats.total}</span>
          </div>
          <div className="flex items-center gap-2">
            <FiCheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-gray-500">Graded:</span>
            <span className="font-semibold text-green-600">{stats.graded}</span>
          </div>
          <div className="flex items-center gap-2">
            <FiClock className="w-4 h-4 text-yellow-500" />
            <span className="text-gray-500">Pending:</span>
            <span className="font-semibold text-yellow-600">{stats.pending}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-gray-200 flex flex-col sm:flex-row gap-3 flex-shrink-0">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by student name or USN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'graded'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Submissions List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : paginatedSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <FiFile className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No submissions found</p>
              <p className="text-gray-400 text-sm">Students haven't submitted yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedSubmissions.map((submission) => (
                <div
                  key={submission._id}
                  className={`bg-white border rounded-lg p-4 transition-colors ${
                    submission.isGraded ? 'border-green-200 bg-green-50/30' : 'border-yellow-200 bg-yellow-50/30'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-bold flex items-center justify-center text-sm flex-shrink-0">
                          {submission.studentName?.charAt(0) || 'S'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{submission.studentName || 'Unknown Student'}</p>
                          <p className="text-xs text-gray-500">USN: {submission.studentUSN || 'N/A'}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          submission.isGraded
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {submission.isGraded ? 'Graded' : 'Pending'}
                        </span>
                        {submission.isLate && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            Late
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <FiClock className="w-3.5 h-3.5" />
                          {new Date(submission.submittedAt).toLocaleString()}
                        </span>
                        {submission.files && submission.files.length > 0 && (
                          <span className="flex items-center gap-1">
                            <FiFile className="w-3.5 h-3.5" />
                            {submission.files.length} file(s)
                          </span>
                        )}
                        {submission.isGraded && (
                          <span className="font-semibold text-green-600">
                            Marks: {submission.marksObtained}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {submission.files && submission.files.map((file, idx) => (
                        <a
                          key={idx}
                          href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${file.fileUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-500 hover:text-indigo-600 rounded-lg transition-colors"
                          title={`Download ${file.fileName}`}
                        >
                          <FiDownload className="w-4 h-4" />
                        </a>
                      ))}
                      {!submission.isGraded && (
                        <button
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setGradingData({ marksObtained: '', feedback: '' });
                            setShowGradeModal(true);
                          }}
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors"
                        >
                          Grade
                        </button>
                      )}
                      {submission.isGraded && submission.status !== 'returned' && (
                        <button
                          onClick={() => handleReturn(submission._id)}
                          className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors"
                        >
                          <FiSend className="w-4 h-4 inline mr-1" />
                          Return
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Grade Modal */}
        {showGradeModal && selectedSubmission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Grade Submission</h4>
                <button
                  onClick={() => setShowGradeModal(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-500">Student: {selectedSubmission.studentName}</p>
                <p className="text-sm text-gray-500">Max Marks: {assignment?.maxMarks}</p>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marks Obtained *
                  </label>
                  <input
                    type="number"
                    value={gradingData.marksObtained}
                    onChange={(e) => setGradingData(prev => ({ ...prev, marksObtained: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    min="0"
                    max={assignment?.maxMarks}
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Max: {assignment?.maxMarks}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Feedback
                  </label>
                  <textarea
                    value={gradingData.feedback}
                    onChange={(e) => setGradingData(prev => ({ ...prev, feedback: e.target.value }))}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Provide feedback to the student..."
                  />
                </div>
                <div className="flex justify-end gap-3 pt-3 border-t">
                  <button
                    onClick={() => setShowGradeModal(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleGrade(selectedSubmission._id)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                  >
                    Submit Grade
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AssignmentSubmissionsModal;